/**
 * usePopulationVisualization — V1.8
 *
 * Hook para visualización de población por segmento de red.
 *
 * Sigue el mismo patrón que useSyntaxVisualization:
 * - Usa feature-state de MapLibre sobre el source `canonical-network`
 * - Feature-state key: `populationValue` (normalizado [0,1])
 * - Rampa de color: spectral (misma que sintaxis)
 * - Percentil-95 como límite superior para clamping de outliers
 * - Preserva estados snapped/selected/hover en la expresión case
 * - Restaura el color original al desactivarse
 */

import { useEffect, useRef, useCallback } from 'react';
import { mapEngine } from '../../map-engine/MapEngineService';
import { usePopulationSegmentSafe } from '../contexts/PopulationSegmentContext';
import { useNetworkStyle } from '../contexts/NetworkStyleContext';
import { COLOR_RAMPS, DEFAULT_SYNTAX_RAMP } from '../config/syntax-color-ramps';
import type { PopulationVariable } from '../contexts/PopulationSegmentContext';

// ============================================================================
// CONSTANTS
// ============================================================================

const EDGES_LAYER_ID = 'canonical-network-edges';
const SOURCE_ID = 'canonical-network';
const POPULATION_VALUE_STATE = 'populationValue';

// ============================================================================
// HELPERS
// ============================================================================

/**
 * Obtiene el valor numérico de una variable para un edge.
 * Las variables `*_per_km` se calculan dividiendo por network_length_m/1000.
 */
function getVariableValue(
  props: Record<string, number | string | null | undefined>,
  variable: PopulationVariable,
): number | null {
  // network_length_m es null para building_refined → fallback a length_meters (de canonical_edges)
  const rawLength = (props.network_length_m ?? props.length_meters) as number | null | undefined;
  const km = rawLength != null && rawLength > 0 ? rawLength / 1000 : null;

  switch (variable) {
    case 'population_per_km': {
      const pop = props.population_estimated as number | null;
      return pop != null && km != null ? pop / km : null;
    }
    case 'population':
      return (props.population_estimated as number | null) ?? null;

    case 'households_per_km': {
      const hh = props.households_estimated as number | null;
      return hh != null && km != null ? hh / km : null;
    }
    case 'households':
      return (props.households_estimated as number | null) ?? null;

    case 'nbi_households_per_km': {
      const nbi = props.nbi_households_estimated as number | null;
      return nbi != null && km != null ? nbi / km : null;
    }
    case 'nbi_households':
      return (props.nbi_households_estimated as number | null) ?? null;
  }
}

// ============================================================================
// HOOK
// ============================================================================

export interface UsePopulationVisualizationResult {
  isActive: boolean;
  currentVariable: PopulationVariable | null;
}

/**
 * Hook para visualizar población por segmento en el mapa usando feature-state.
 *
 * Llamar desde MapLayoutContent (mismo nivel que useSyntaxVisualization).
 * El hook es no-op si PopulationSegmentProvider no está presente.
 */
export function usePopulationVisualization(): UsePopulationVisualizationResult {
  const ctx = usePopulationSegmentSafe();
  const { style } = useNetworkStyle();

  const isActiveRef = useRef(false);
  const lastVariableRef = useRef<PopulationVariable | null>(null);
  const appliedEdgeIdsRef = useRef<Set<string>>(new Set());

  const populationModeActive = ctx?.populationModeActive ?? false;
  const selectedVariable = ctx?.selectedVariable ?? 'population_per_km';
  const edgeData = ctx?.edgeData ?? new Map();
  const ranges = ctx?.ranges ?? { population_per_km: null, population: null, households_per_km: null, households: null, nbi_households_per_km: null, nbi_households: null };
  const runStatus = ctx?.runStatus ?? 'no_runs';

  const isReady = runStatus === 'completed' && edgeData.size > 0;

  /**
   * Aplica valores normalizados a los feature-states de los edges.
   */
  const applyFeatureStates = useCallback(() => {
    const map = mapEngine.getMap();
    if (!map || !map.getSource(SOURCE_ID)) return;

    const range = ranges[selectedVariable];
    if (!range) return;

    // Usar p95 como tope superior para proteger de outliers
    const effectiveMax = range.p95 > range.min ? range.p95 : range.max;
    const span = effectiveMax - range.min || 1;

    const newAppliedIds = new Set<string>();

    for (const [edgeId, props] of edgeData.entries()) {
      const raw = getVariableValue(props as Record<string, number | string | null | undefined>, selectedVariable);
      if (raw === null) continue;

      const normalized = Math.min(Math.max((raw - range.min) / span, 0), 1);

      try {
        map.setFeatureState(
          { source: SOURCE_ID, id: edgeId },
          { [POPULATION_VALUE_STATE]: normalized },
        );
        newAppliedIds.add(edgeId);
      } catch {
        // edge aún no en source — ignorar
      }
    }

    // Limpiar edges que ya no tienen datos
    for (const oldId of appliedEdgeIdsRef.current) {
      if (!newAppliedIds.has(oldId)) {
        try {
          map.removeFeatureState({ source: SOURCE_ID, id: oldId }, POPULATION_VALUE_STATE);
        } catch {
          // ignorar
        }
      }
    }

    appliedEdgeIdsRef.current = newAppliedIds;

    console.log('[usePopulationVisualization] Feature-state applied to', newAppliedIds.size, 'edges', {
      variable: selectedVariable,
      range: { min: range.min, p95: effectiveMax },
    });
  }, [edgeData, ranges, selectedVariable]);

  /**
   * Aplica la rampa de color al layer de edges.
   */
  const applyColorRamp = useCallback(() => {
    const map = mapEngine.getMap();
    if (!map || !map.getLayer(EDGES_LAYER_ID)) return;

    const ramp = COLOR_RAMPS[DEFAULT_SYNTAX_RAMP];

    const colorExpr: unknown[] = [
      'interpolate',
      ['linear'],
      ['coalesce', ['feature-state', POPULATION_VALUE_STATE], 0],
      ...ramp.stops.flatMap((s) => [s.stop, s.color]),
    ];

    const finalExpr = [
      'case',
      ['boolean', ['feature-state', 'snapped'], false],
      '#2196F3',
      ['boolean', ['feature-state', 'selected'], false],
      '#2FA4B5',
      ['boolean', ['feature-state', 'hover'], false],
      '#60D0E8',
      colorExpr,
    ];

    map.setPaintProperty(EDGES_LAYER_ID, 'line-color', finalExpr);

    console.log('[usePopulationVisualization] Color ramp applied for variable:', selectedVariable);
  }, [selectedVariable]);

  /**
   * Limpia todos los feature-states de población.
   */
  const clearFeatureStates = useCallback(() => {
    const map = mapEngine.getMap();
    if (!map || !map.getSource(SOURCE_ID)) return;

    for (const edgeId of appliedEdgeIdsRef.current) {
      try {
        map.removeFeatureState({ source: SOURCE_ID, id: edgeId }, POPULATION_VALUE_STATE);
      } catch {
        // ignorar
      }
    }
    appliedEdgeIdsRef.current.clear();
  }, []);

  /**
   * Restaura el color original del layer.
   */
  const restoreOriginalColor = useCallback(() => {
    const map = mapEngine.getMap();
    if (!map || !map.getLayer(EDGES_LAYER_ID)) return;

    const originalColorExpr = [
      'case',
      ['boolean', ['feature-state', 'associated'], false],
      '#f59e0b',
      ['boolean', ['feature-state', 'snapped'], false],
      '#2196F3',
      ['boolean', ['feature-state', 'selected'], false],
      '#2FA4B5',
      ['boolean', ['feature-state', 'hover'], false],
      '#60D0E8',
      style.edges.color,
    ];

    map.setPaintProperty(EDGES_LAYER_ID, 'line-color', originalColorExpr);
    clearFeatureStates();

    console.log('[usePopulationVisualization] Original color restored');
  }, [style.edges.color, clearFeatureStates]);

  // ── Activar / desactivar visualización ──
  useEffect(() => {
    if (populationModeActive && isReady) {
      applyFeatureStates();
      applyColorRamp();
      isActiveRef.current = true;
      lastVariableRef.current = selectedVariable;
      console.log('[usePopulationVisualization] Population visualization ACTIVATED');
    } else if (!populationModeActive && isActiveRef.current) {
      restoreOriginalColor();
      isActiveRef.current = false;
      lastVariableRef.current = null;
      console.log('[usePopulationVisualization] Population visualization DEACTIVATED');
    }
  }, [
    populationModeActive,
    isReady,
    selectedVariable,
    applyFeatureStates,
    applyColorRamp,
    restoreOriginalColor,
  ]);

  // ── Cambio de variable mientras está activo ──
  useEffect(() => {
    if (populationModeActive && isActiveRef.current && lastVariableRef.current !== selectedVariable) {
      applyFeatureStates();
      applyColorRamp();
      lastVariableRef.current = selectedVariable;
      console.log('[usePopulationVisualization] Variable changed to:', selectedVariable);
    }
  }, [selectedVariable, populationModeActive, applyFeatureStates, applyColorRamp]);

  // ── Cleanup al desmontar ──
  useEffect(() => {
    return () => {
      if (isActiveRef.current) {
        restoreOriginalColor();
      }
    };
  }, [restoreOriginalColor]);

  return {
    isActive: populationModeActive && isReady,
    currentVariable: populationModeActive ? selectedVariable : null,
  };
}
